export const useConfirmationDialogStore = defineStore('confirmationDialog', () => {
  const confirmationDialogMessage = ref('');
  const confirmationDialogOpen = ref(false);

  let confirmationDialogPromise: Promise<boolean> | undefined;
  let resolveConfirmationDialogPromise: ((response: boolean) => void) | undefined;

  /**
   * Given a message, opens the confirmation dialog and sets up the
   * promise in the background so that `setConfirmationResponse`
   * will resolve it and close the dialog.
   *
   * Returns the promise set up behind the scenes, which can be
   * `await`ed to wait for the user's response before taking action.
   *
   * @example
   * const doSomethingWithConfirmation = async () => {
   *   const response = await promptForConfirmation('Are you sure?');
   *   if (response) {
   *     doSomething();
   *   }
   * };
   *
   * @param message
   */
  const promptForConfirmation = (message: string) => {
    confirmationDialogMessage.value = message;
    confirmationDialogOpen.value = true;
    confirmationDialogPromise = new Promise(resolve => {
      resolveConfirmationDialogPromise = response => {
        resolve(response);
        confirmationDialogPromise = undefined;
        resolveConfirmationDialogPromise = undefined;
      };
    });

    return confirmationDialogPromise;
  };

  /**
   * If there's an existing promise for the confirmation dialog,
   * resolve it with the given value, then close the dialog and
   * clear the message ref.
   *
   * @param response
   */
  const setConfirmationResponse = (response: boolean) => {
    if (!resolveConfirmationDialogPromise) {
      return;
    }

    resolveConfirmationDialogPromise(response);
    confirmationDialogOpen.value = false;
    // Wait until the dialog is closed, then clear the message
    setTimeout(() => {
      confirmationDialogMessage.value = '';
    }, 200);
  };

  return {
    confirmationDialogMessage,
    confirmationDialogOpen,
    promptForConfirmation,
    setConfirmationResponse,
  };
});
